import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Home } from 'lucide-react';
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from './breadcrumb';

// Route configuration for breadcrumb labels and hierarchy
const routeConfig = {
  // Staff routes
  '/staff/dashboard': { label: 'Dashboard', parent: null, icon: Home },
  '/staff/customers': { label: 'Customers', parent: '/staff/dashboard' },
  '/staff/customers/broadcasts': { label: 'Customer Broadcasts', parent: '/staff/customers' },
  '/staff/loans': { label: 'Active Loans', parent: '/staff/loans-hub' },
  '/staff/loans-hub': { label: 'Loans Hub', parent: '/staff/dashboard' },
  '/staff/loans-hub?tab=active': { label: 'Active Loans', parent: '/staff/loans-hub' },
  '/staff/loans-hub?tab=applications': { label: 'Applications', parent: '/staff/loans-hub' },
  '/staff/loans-hub?tab=declined': { label: 'Declined Loans', parent: '/staff/loans-hub' },
  '/staff/loans/reconciliation': { label: 'Loan Dedukt Reconciliation', parent: '/staff/loans-hub?tab=active' },
  '/staff/applications': { label: 'Applications', parent: '/staff/loans-hub' },
  '/staff/payments': { label: 'Payments', parent: '/staff/dashboard' },
  '/staff/refunds': { label: 'Refunds', parent: '/staff/dashboard' },
  '/staff/expenses': { label: 'Expenses', parent: '/staff/dashboard' },
  '/staff/branches': { label: 'Branches', parent: '/staff/dashboard' },
  '/staff/team': { label: 'Team', parent: '/staff/dashboard' },
  '/staff/reports': { label: 'Reports', parent: '/staff/dashboard' },
  '/staff/analytics': { label: 'Analytics', parent: '/staff/dashboard' },
  '/staff/income': { label: 'Income', parent: '/staff/dashboard' },
  '/staff/my-attendance': { label: 'My Attendance', parent: '/staff/dashboard' },
  '/staff/attendance': { label: 'Attendance Register', parent: '/staff/dashboard' },
  '/staff/activity-logs': { label: 'Activity Logs', parent: '/staff/dashboard' },
  '/staff/my-loan': { label: 'My Staff Loan', parent: '/staff/dashboard' },
  '/staff/my-payslips': { label: 'My Payslips', parent: '/staff/dashboard' },
  '/staff/profile': { label: 'Profile', parent: '/staff/dashboard' },
  
  // Admin routes
  '/admin/staff-loans': { label: 'Staff Loans', parent: '/staff/dashboard' },
  '/admin/payroll': { label: 'Payroll', parent: '/staff/dashboard' },
  '/admin/performance-reports': { label: 'Performance Reports', parent: '/staff/dashboard' },
  '/admin/settings': { label: 'Settings', parent: '/staff/dashboard' },
  
  // Customer routes
  '/customer/dashboard': { label: 'Dashboard', parent: null, icon: Home },
  '/customer/loans': { label: 'My Loans', parent: '/customer/dashboard' },
  '/customer/applications': { label: 'Applications', parent: '/customer/dashboard' },
  '/customer/payments': { label: 'Payments', parent: '/customer/dashboard' },
  '/customer/refunds': { label: 'Refunds', parent: '/customer/dashboard' },
  '/customer/profile': { label: 'Profile', parent: '/customer/dashboard' },
};

// Dynamic route patterns
// `parent` may be a string OR a function (search) => path so the breadcrumb
// can branch based on URL query string (e.g. `?from=declined`).
const dynamicRoutes = [
  { pattern: /^\/staff\/customers\/(.+)$/, label: 'Customer Details', parent: '/staff/customers' },
  { pattern: /^\/staff\/loans\/(.+)$/, label: 'Loan Details', parent: '/staff/loans-hub?tab=active' },
  {
    pattern: /^\/staff\/applications\/(.+)$/,
    label: 'Application Details',
    parent: (search) => {
      const from = (search || '').match(/[?&]from=([^&]+)/);
      if (from && from[1] === 'declined') return '/staff/loans-hub?tab=declined';
      if (from && from[1] === 'active') return '/staff/loans-hub?tab=active';
      if (from && from[1] === 'applications') return '/staff/loans-hub?tab=applications';
      return '/staff/loans-hub';
    },
  },
  { pattern: /^\/staff\/staff\/(.+)$/, label: 'Staff Details', parent: '/staff/team' },
  { pattern: /^\/customer\/loans\/(.+)$/, label: 'Loan Details', parent: '/customer/loans' },
];

// Get breadcrumb trail from current path
function getBreadcrumbTrail(pathname, search = '') {
  const trail = [];
  let currentPath = pathname;
  
  // Check for dynamic routes first
  let config = routeConfig[currentPath];
  if (!config) {
    for (const route of dynamicRoutes) {
      if (route.pattern.test(currentPath)) {
        trail.unshift({ 
          path: currentPath, 
          label: route.label, 
          isCurrent: true 
        });
        const parentPath = typeof route.parent === 'function' ? route.parent(search) : route.parent;
        currentPath = parentPath;
        config = routeConfig[currentPath];
        break;
      }
    }
  }
  
  // Build trail from current path up to root
  while (config) {
    trail.unshift({ 
      path: currentPath, 
      label: config.label, 
      icon: config.icon,
      isCurrent: trail.length === 0 
    });
    
    if (config.parent) {
      currentPath = config.parent;
      config = routeConfig[currentPath];
    } else {
      break;
    }
  }
  
  return trail;
}

export function AutoBreadcrumb({ className = '' }) {
  const location = useLocation();
  const trail = getBreadcrumbTrail(location.pathname, location.search);
  
  // Don't show breadcrumb on dashboard (root) pages
  if (trail.length <= 1) return null;
  
  return (
    <Breadcrumb className={className} data-testid="auto-breadcrumb">
      <BreadcrumbList>
        {trail.map((crumb, index) => {
          const IconComponent = crumb.icon;
          return (
            <React.Fragment key={crumb.path}>
              {index > 0 && <BreadcrumbSeparator />}
              <BreadcrumbItem>
                {crumb.isCurrent ? (
                  <BreadcrumbPage>{crumb.label}</BreadcrumbPage>
                ) : (
                  <BreadcrumbLink asChild>
                    <Link to={crumb.path} className="flex items-center gap-1">
                      {IconComponent && <IconComponent className="w-3.5 h-3.5" />}
                      <span className={IconComponent ? 'hidden sm:inline' : ''}>{crumb.label}</span>
                    </Link>
                  </BreadcrumbLink>
                )}
              </BreadcrumbItem>
            </React.Fragment>
          );
        })}
      </BreadcrumbList>
    </Breadcrumb>
  );
}

export default AutoBreadcrumb;
